import React, { useRef, ReactNode, useEffect, useCallback } from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiPlus } from '@lumapps/lumx/icons';
import { Button, Emphasis } from '@lumapps/lumx/react';
import { NavigationMapItem } from '@lumapps/navigation/types';
import { useTranslate } from '@lumapps/translations';

import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import { NAVIGATION_ADMIN } from '../../keys';
import { NavigationItemWizardOptionProps } from '../../types';
import { NavigationItemModalProps } from '../NavigationItemModal/NavigationItemModal';
import { NavigationItemWizard } from '../NavigationItemWizard';
import { NavigationItemWizardOptions } from '../NavigationItemWizardOptions';

export interface AddNavigationItemProps {
    /** default language for the platform */
    defaultLanguage: string;
    /** list of platform languages */
    languages: string[];
    /** callback to be executed once the navigation item is created */
    onAdded: (navItem: NavigationMapItem, buttonRef: any) => void;
    /** whether the button is disabled or not */
    isDisabled?: boolean;
    /** if there is a navigation item type that should not be created, it needs to be specified here. */
    options?: NavigationItemWizardOptionProps['options'];
    /** scope to be used for tracking purposes */
    scope: string;
    /** component to be displayed before the wizard options */
    beforeOptions?: ReactNode;
    /** Override default parameters for all NavigationItemModals */
    navigationItemModalProps?: Partial<NavigationItemModalProps>;
    /** Define if the wizard should be open by default */
    isOpen?: boolean;
    /** Callback to be sent on toggle modal */
    onToggleWizard?(isOpen: boolean): void;
}

/**
 * Button that triggers the flow for adding a new navigation item.
 * @param props - AddNavigationItemProps
 */
export const AddNavigationItem: React.FC<AddNavigationItemProps> = ({
    defaultLanguage,
    languages,
    onAdded,
    isDisabled = false,
    options,
    scope,
    beforeOptions,
    navigationItemModalProps,
    isOpen,
    onToggleWizard,
}) => {
    const { translateKey } = useTranslate();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isWizardOpen, setIsWizardOpen] = React.useState(false);
    const { get } = useDataAttributes(scope);

    const { fetchAtInstanceLevel } = useNavigationAdminConfiguration();

    /**
     * This state prevents the NavigationItemWizard from closing when one of the wizard options is selected
     * and another dialog is displayed. Due to how DS dropdowns are constructed (using portals), opening a portal
     * inside the sub dialog and clicking inside of it means that it is a click outside of NavigationItemWizard
     * thus closing the Dialog. This prevents that from happening.
     */
    const [wasOptionSelected, setWasOptionSelected] = React.useState(false);

    const toggleWizard = useCallback(
        (open: boolean) => {
            setIsWizardOpen(open);
            if (onToggleWizard) {
                onToggleWizard(open);
            }
        },
        [onToggleWizard],
    );

    /**
     * Callback to be executed once the navigation item is added. When that happens, we let the callback passed into
     * this component know, as well as toggling off the wizard and flagging that there is no option selected.
     * @param navItem
     */
    const onNavigationItemAdded = (navItem: NavigationMapItem) => {
        onAdded(navItem, buttonRef);
        setWasOptionSelected(false);
        toggleWizard(false);
    };

    useEffect(() => {
        if (isOpen) {
            toggleWizard(isOpen);
        }
    }, [isOpen, toggleWizard]);

    return (
        <>
            <Button
                emphasis={Emphasis.medium}
                onClick={() => toggleWizard(true)}
                ref={buttonRef}
                isDisabled={isDisabled}
                leftIcon={mdiPlus}
                {...get({
                    element: 'add-navigation-item',
                })}
            >
                {translateKey(NAVIGATION_ADMIN.ADD_NAVIGATION_ITEM)}
            </Button>
            <NavigationItemWizard
                isOpen={isWizardOpen}
                buttonRef={buttonRef}
                onClose={() => toggleWizard(false)}
                preventAutoClose={wasOptionSelected}
                beforeOptions={beforeOptions}
                options={
                    <NavigationItemWizardOptions
                        fetchAtInstanceLevel={fetchAtInstanceLevel}
                        defaultLanguage={defaultLanguage}
                        languages={languages}
                        onAdded={onNavigationItemAdded}
                        toggleWizard={toggleWizard}
                        setWasOptionSelected={setWasOptionSelected}
                        options={options}
                        scope={scope}
                        navigationItemModalProps={navigationItemModalProps}
                    />
                }
            />
        </>
    );
};
